import "@repo/utils/global/polyfills";

import { getOwner, JSX, onCleanup, onMount, Show, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import c from "class-c";
import { Motion, Presence } from "solid-motionone";
import overlayApi from "solid-overlays";
import FeatureStore from "@rock-solid/feature-store";

import PendingMigrationError from "@repo/api/PendingMigrationError";
import Devtools from "@repo/devtools";
import Telemetry from "@repo/telemetry";
import Env from "@repo/utils-client/Env";
import Platform from "@repo/utils-client/Platform";
import AppRouter from "@repo/utils-solid/AppRouter";
import UOwner from "@repo/utils-solid/UOwner";

import Button from "../buttons/Button";
import ContainerButton from "../buttons/ContainerButton";
import LoadableButton from "../buttons/LoadableButton";
import { ErrorProvider, useError } from "../error/ErrorProvider";
import { ErrorContext } from "../error/ErrorProvider";
import HydrationFallback from "../flow/HydrationFallback";
import Alert from "../info/Alert";

import { invalidate, remount } from "./signals";

import "solid-overlays/index.css";
import "./GlobalRoot.scss";

FeatureStore.configure({
  ErrorContext,
  cachePendingActions: true,
  useCaptureError() {
    const error = useError();
    return (e) => {
      if (!(typeof e === "string" || e instanceof Error))
        throw new Error(
          `Attempted to capture error with unsupported type: ${e}`
        );
      return error.capture(e);
    };
  },
  hooks: {
    init(featureName, initialState) {
      Devtools.setFeatureStore([featureName], initialState);
    },
    setStore(featureName, path, value) {
      Devtools.setFeatureStore([featureName, ...path], value);
    },
  },
});

{
  const prefixes = {
    "m-": [],
    "mx-": ["m-"],
    "my-": ["m-"],
    "mt-": ["m-", "my-"],
    "mb-": ["m-", "my-"],
    "ml-": ["m-", "mx-"],
    "mr-": ["m-", "mx-"],
    "p-": [],
    "px-": ["p-"],
    "py-": ["p-"],
    "pt-": ["p-", "py-"],
    "pb-": ["p-", "py-"],
    "pl-": ["p-", "px-"],
    "pr-": ["p-", "px-"],
    "text:": [],
    "color:": [],
    "bg:": [],
    "z-": [],
    "position-": [],
  } as Record<string, string[]>;
  c.config.transform = (str) => {
    const encountered = new Set<string>();

    return str
      .split(" ")
      .reverse()
      .filter((className) => {
        const prefix = className.includes(":")
          ? `${className.split(":", 1)[0]}:`
          : `${className.split("-", 1)[0]}-`;

        if (prefix in prefixes) {
          if (prefix === "text:" && className.startsWith("text:+")) {
            if (encountered.has("text:+")) return false;
            return true;
          }
          if (encountered.has(prefix)) return false;
          for (const p of prefixes[prefix]!) {
            if (encountered.has(p)) return false;
          }
          encountered.add(prefix);
        }
        return true;
      })
      .reverse()
      .join(" ");
  };
}

declare namespace GlobalRoot {
  type Props = {
    OverlaysProvider: ReturnType<
      ReturnType<typeof overlayApi<{}>>["create"]
    >["OverlaysProvider"];
    devtools?: boolean;
    children: JSX.Element;
  };
}

function GlobalRoot(props: GlobalRoot.Props) {
  const [store, setStore] = createStore({
    error: null as string | Error | null,
    show: false,
  });

  onMount(() => {
    if (Env.debug) {
      Object.assign(window, {
        dev: { AppRouter, Telemetry },
      });
    }
  });

  return (
    <HydrationFallback>
      <div id="global-root">
        {(() => (
          invalidate.listen(),
          (
            <ErrorProvider
              onError={(error) => {
                if (error == null) return;

                if (!(typeof error === "string" || error instanceof Error))
                  return Telemetry.captureError(
                    new Error("Received unexpected error type.", {
                      cause: error,
                    }),
                    {
                      extra: { error },
                      tags: {
                        errorString:
                          // String constructor covers symbols
                          String(error),
                        errorJson: (() => {
                          try {
                            return JSON.stringify(error);
                          } catch (e) {
                            return "Failed to stringify error.";
                          }
                        })(),
                      },
                    }
                  );

                // Don't show empty error message
                if (!(typeof error === "string" ? error : error.message))
                  return;
                setStore({ error, show: !!error });
              }}
            >
              <props.OverlaysProvider>
                <FeatureStore.Provider>
                  {(remount.listen(), props.children)}
                  {
                    (() => {
                      const owner = getOwner();
                      if (Platform.isClient && props.devtools) {
                        Devtools.on("init", () => {
                          UOwner.run(owner, () => {
                            const featureContext = useContext(
                              FeatureStore.Context
                            )!;
                            const [store] = featureContext;
                            Devtools.setFeatureStore([], store);
                          });
                        });
                      }
                    })() as never
                  }
                </FeatureStore.Provider>
              </props.OverlaysProvider>
              <Presence>
                <Show when={store.show}>
                  <Motion.div
                    id="error"
                    class="center-child"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 24 }}
                  >
                    <ContainerButton
                      class="fit-content"
                      onClick={() => {
                        setStore("show", false);
                      }}
                    >
                      <Alert type="error" class="p-2">
                        {(() => {
                          let { error } = store;
                          if (!error) return;

                          // PendingMigrationError may be wrapped in ConnectError
                          if (
                            error instanceof Error &&
                            error.cause instanceof PendingMigrationError
                          )
                            error = error.cause;
                          if (
                            import.meta.env.DEV &&
                            error instanceof PendingMigrationError
                          )
                            return (
                              <div class="row-center">
                                {error.message}
                                <LoadableButton
                                  as={Button}
                                  size="small"
                                  variant="primary-hard"
                                  type="submit"
                                  style={{ margin: "-8px 0 -8px 16px" }}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    return error.fix().then(() => {
                                      LoadableButton.afterSuccess(() => {
                                        setStore("show", false);
                                        invalidate();
                                      });
                                    });
                                  }}
                                >
                                  Run migrations
                                </LoadableButton>
                              </div>
                            );

                          // We intentionally don't want to time out migration error
                          const t = setTimeout(() => {
                            setStore("show", false);
                          }, 5000);

                          onCleanup(() => {
                            clearTimeout(t);
                          });

                          if (typeof error === "string") return error;
                          return error.message;
                        })()}
                      </Alert>
                    </ContainerButton>
                  </Motion.div>
                </Show>
              </Presence>
            </ErrorProvider>
          )
        ))()}
      </div>
    </HydrationFallback>
  );
}

export default GlobalRoot;
